import { useState, useEffect, useCallback } from 'react';
import {
  agentService,
  productService,
  orderService,
  conversationService,
} from '../api/services';

// Extrait un nombre fiable depuis une réponse API.
// Les endpoints renvoient { success, data: [...] } et parfois un total de pagination.
const extractCount = (response) => {
  if (!response) return 0;

  // Préférer un total de pagination s'il existe (compte global, pas juste la page courante)
  const pagination = response.pagination || response.meta || {};
  if (typeof pagination.total === 'number') return pagination.total;
  if (typeof pagination.totalItems === 'number') return pagination.totalItems;
  if (typeof response.total === 'number') return response.total;

  const data = response.data ?? response;
  if (Array.isArray(data)) return data.length;
  if (Array.isArray(data?.agents)) return data.agents.length;
  if (Array.isArray(data?.items)) return data.items.length;
  return 0;
};

/**
 * Récupère les compteurs réels affichés dans les badges de la sidebar.
 * Chaque appel est isolé : si un endpoint échoue, les autres compteurs
 * restent valides (Promise.allSettled).
 */
export default function useSidebarCounts() {
  const [counts, setCounts] = useState({
    agents: 0,
    produits: 0,
    commandes: 0,
    conversations: 0,
  });

  const fetchCounts = useCallback(async () => {
    const [agents, produits, commandes, conversations] =
      await Promise.allSettled([
        agentService.getAgents({ limit: 1000 }),
        productService.getProducts({ limit: 1000 }),
        orderService.getOrders({ limit: 1000 }),
        conversationService.getConversations({ limit: 1000 }),
      ]);

    setCounts((prev) => ({
      agents: agents.status === 'fulfilled' ? extractCount(agents.value) : prev.agents,
      produits: produits.status === 'fulfilled' ? extractCount(produits.value) : prev.produits,
      commandes: commandes.status === 'fulfilled' ? extractCount(commandes.value) : prev.commandes,
      conversations:
        conversations.status === 'fulfilled' ? extractCount(conversations.value) : prev.conversations,
    }));
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return { counts, refetch: fetchCounts };
}
